/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Form, Card, Row, Col, Accordion } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faGlobe,
  faLanguage,
  faClock,
  faLink,
  faCalendar,
  faCog,
  faBolt,
  faC
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import HVAToast from 'components/common/HVAToast';
import { searchKnowledgeBase } from '../../services/knowledgehub.service';

// Definir la interfaz para los resultados web
interface WebSearchResult {
  text: string; // Texto formateado del resultado
  relevance: number; // Score de relevancia
  title?: string; // Título del resultado
  link?: string; // URL del resultado
  snippet?: string; // Snippet/preview
  displayLink?: string; // Dominio mostrable
  formattedUrl?: string; // URL formateada
  publishedDate?: string; // Fecha de publicación si está disponible
}

const WebSearchPlayground: React.FC = () => {
  // Estados del componente
  const [query, setQuery] = useState('');
  const [maxResults, setMaxResults] = useState(5);
  const [language, setLanguage] = useState('es');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<WebSearchResult[]>([]);
  const [totalFound, setTotalFound] = useState(0);
  const [processingTime, setProcessingTime] = useState<number | null>(null);
  const [searchProvider, setSearchProvider] = useState<string>('');

  // Toast states
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Función para realizar la búsqueda
  const handleSearch = async () => {
    if (!query.trim()) {
      showToastMessage('Please enter a search query', 'warning');
      return;
    }

    try {
      setSearching(true);
      setResults([]);
      setTotalFound(0);
      setProcessingTime(null);
      setSearchProvider('');

      console.log('Starting web search with params:', {
        query,
        max_results: maxResults,
        language,
        search_type: 'web'
      });

      const startTime = Date.now();

      // Llamar al servicio con search_type: 'web'
      const response = await searchKnowledgeBase({
        query,
        max_results: maxResults,
        search_type: 'web' as any, // Forzar el tipo
        language
      } as any);

      const endTime = Date.now();

      console.log('Web search response:', response);

      // Procesar la respuesta
      setProcessingTime(response.processing_time || endTime - startTime);
      setResults(response.results || []);
      setTotalFound(response.total_found || response.results?.length || 0);
      setSearchProvider('Web Search');

      if (!response.results || response.results.length === 0) {
        showToastMessage('No results found. Try adjusting your query.', 'info');
      } else {
        showToastMessage(`Found ${response.results.length} results`, 'success');
      }
    } catch (err: any) {
      console.error('Web search error:', err);
      showToastMessage(
        err.response?.data?.message || 'Error performing web search',
        'error'
      );
    } finally {
      setSearching(false);
    }
  };

  // Manejar Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Función para extraer información del resultado
  const parseResultText = (result: WebSearchResult) => {
    // El texto viene formateado desde el backend, intentar parsearlo
    const lines = result.text?.split('\n') || [];
    const parsed: any = {
      title: '',
      source: '',
      date: '',
      content: '',
      url: ''
    };

    lines.forEach((line: string) => {
      if (line.startsWith('Resultado')) {
        parsed.title = line.replace(/^Resultado \d+:\s*/, '');
      } else if (line.startsWith('Fuente:')) {
        parsed.source = line.replace('Fuente:', '').trim();
      } else if (line.startsWith('Fecha:')) {
        parsed.date = line.replace('Fecha:', '').trim();
      } else if (line.startsWith('Contenido:')) {
        parsed.content = line.replace('Contenido:', '').trim();
      } else if (line.startsWith('Resumen:')) {
        parsed.content = line.replace('Resumen:', '').trim();
      } else if (line.startsWith('URL:')) {
        parsed.url = line.replace('URL:', '').trim();
      }
    });

    return parsed;
  };

  // Función para obtener color según relevancia
  const getRelevanceColor = (relevance: number) => {
    if (relevance >= 0.8) return 'success';
    if (relevance >= 0.6) return 'warning';
    return 'secondary';
  };

  return (
    <div className="pt-3">
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Search Form */}
      <Card className="mb-4">
        <Card.Header className="bg-body-secondary pt-3 pb-3">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faGlobe} className="me-2" />
            Web Search
          </h5>
        </Card.Header>
        <Card.Body className="px-6 bg-body">
          <Form>
            <Row className="mb-3">
              {/* Language Selector */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon icon={faLanguage} className="me-2" />
                    Language
                  </Form.Label>
                  <Form.Select
                    value={language}
                    onChange={e => setLanguage(e.target.value)}
                    disabled={searching}
                  >
                    <option value="es">Spanish (Español)</option>
                    <option value="el">Greek (Ελληνικά)</option>
                    <option value="en">English</option>
                    <option value="fr">French (Français)</option>
                    <option value="de">German (Deutsch)</option>
                    <option value="it">Italian (Italiano)</option>
                    <option value="pt">Portuguese (Português)</option>
                    <option value="nl">Dutch (Nederlands)</option>
                    <option value="pl">Polish (Polski)</option>
                    <option value="ru">Russian (Русский)</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Max Results */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-body-emphasis fw-semibold">
                    <FontAwesomeIcon icon={faCog} className="me-2" />
                    Max Results
                  </Form.Label>
                  <Form.Select
                    value={maxResults}
                    onChange={e => setMaxResults(Number(e.target.value))}
                    disabled={searching}
                  >
                    <option value={3}>3 results</option>
                    <option value={5}>5 results</option>
                    <option value={10}>10 results</option>
                    <option value={20}>20 results</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Search Query */}
            <Form.Group className="mb-3">
              <Form.Label className="text-body-emphasis fw-semibold">
                <FontAwesomeIcon icon={faBolt} className="me-2" />
                Search Query
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter your web search query..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={searching}
                className="fs-9"
                style={{ resize: 'vertical' }}
              />
              <Form.Text className="text-muted">
                Press Enter to search or click the Search button
              </Form.Text>
            </Form.Group>

            {/* Search Button */}
            <div className="d-flex justify-content-end">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSearch}
                disabled={searching || !query.trim()}
                className="px-4"
              >
                {searching ? (
                  <>
                    <FontAwesomeIcon icon={faC} className="me-1" spin />
                    Searching Web...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Search Web
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {/* Search Results */}
      {results.length > 0 && (
        <Card>
          <Card.Header className="bg-body-secondary pt-3 pb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="fw-bold pe-2">Web Search Results</span>
                {processingTime !== null && (
                  <span className="text-body-tertiary small">
                    {' ( '}
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {processingTime}
                    {'ms )'}
                  </span>
                )}
                {searchProvider && (
                  <Badge bg="info" variant="phoenix" className="mx-2">
                    via {searchProvider}
                  </Badge>
                )}
                {totalFound > 0 && (
                  <Badge bg="primary" variant="phoenix" className="mx-2">
                    {totalFound} results found
                  </Badge>
                )}
              </div>
            </div>
          </Card.Header>
          <Card.Body className="px-6 bg-body">
            <Accordion>
              {results.map((result, index) => {
                const parsed = parseResultText(result);

                return (
                  <Accordion.Item key={index} eventKey={String(index)}>
                    <Accordion.Header>
                      <div className="d-flex justify-content-between align-items-center w-100 me-3">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon
                            icon={faGlobe}
                            className="me-2 text-body-tertiary fs-8"
                          />
                          <span className="fw-medium text-body-emphasis fs-9">
                            {parsed.title || `Result ${index + 1}`}
                          </span>
                          {parsed.source && (
                            <Badge
                              bg="secondary"
                              variant="phoenix"
                              text="dark"
                              className="ms-2"
                            >
                              {parsed.source}
                            </Badge>
                          )}
                        </div>
                        <Badge bg={getRelevanceColor(result.relevance)} pill>
                          {Math.round(result.relevance * 100)}%
                        </Badge>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="p-3 bg-body-emphasis rounded fs-9 mb-3">
                        <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                          {parsed.content || result.snippet || result.text}
                        </p>
                      </div>

                      <div className="text-body-tertiary small">
                        {parsed.source && (
                          <div>
                            <FontAwesomeIcon icon={faGlobe} className="me-2" />
                            <strong>Source:</strong> {parsed.source}
                          </div>
                        )}
                        {parsed.date && (
                          <div>
                            <FontAwesomeIcon
                              icon={faCalendar}
                              className="me-2"
                            />
                            <strong>Published:</strong> {parsed.date}
                          </div>
                        )}
                        {parsed.url && (
                          <div className="mt-2">
                            <FontAwesomeIcon icon={faLink} className="me-2" />
                            <a
                              href={parsed.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              {parsed.url}
                            </a>
                          </div>
                        )}
                        <div className="mt-2">
                          <strong>Relevance Score:</strong>{' '}
                          {(result.relevance * 100).toFixed(2)}%
                        </div>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Card.Body>
        </Card>
      )}

      {/* No results message */}
      {!searching && results.length === 0 && query && (
        <Card>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon
              icon={faGlobe}
              size="2x"
              className="text-body-tertiary mb-3"
            />
            <p className="text-body-tertiary">
              No search performed yet. Enter a query and click Search.
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default WebSearchPlayground;
